import React, { useCallback, useMemo, useReducer, useRef, useState } from "react"
import { useFetcher } from "react-router"
import { z } from "zod"
import { LoaderCircleIcon, SaveIcon, PlusIcon, PencilIcon, TrashIcon, XCircleIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"
import { Badge } from "@/components/ui/badge"

import type { EnhancedConfigData } from "./brand-lenses-configuration"

interface FetcherData {
  success?: boolean
  action?: string
  errorMessage?: string
}

const fieldBaseSchema = z.object({
  description: z.string().min(1, "Description is required"),
  order: z.coerce.number().optional(),
  isGroupEnd: z.coerce.boolean().optional(),
})

const textFilterSchema = fieldBaseSchema.extend({
  type: z.literal("text"),
  maxLength: z.coerce.number().optional(),
  placeholder: z.string().optional(),
  defaultValue: z.string().optional(),
})

const autocompleteFilterSchema = fieldBaseSchema.extend({
  type: z.literal("autocomplete"),
  multiple: z.coerce.boolean().optional(),
})

const selectFilterSchema = fieldBaseSchema.extend({
  type: z.literal("select"),
  options: z
    .array(
      z.object({
        value: z.union([z.string(), z.number()]),
        label: z.string().optional(),
      }),
    )
    .default([]),
  multiple: z.coerce.boolean().optional(),
  defaultValue: z.union([z.string(), z.array(z.string())]).optional(),
  parseAs: z.enum(["literal", "string"]).optional(),
})

const dateRangeFilterSchema = fieldBaseSchema.extend({
  type: z.literal("date-range"),
  options: z
    .array(
      z.object({
        name: z.string(),
        diff: z.coerce.number(),
      }),
    )
    .optional(),
})

const timeRangeFilterSchema = fieldBaseSchema.extend({
  type: z.literal("time-range"),
})

const booleanFilterSchema = fieldBaseSchema.extend({
  type: z.literal("boolean"),
  placeholder: z.string().optional(),
  options: z.tuple([
    z.object({ value: z.literal(true), label: z.string() }),
    z.object({ value: z.literal(false), label: z.string() }),
  ]),
  defaultValue: z.coerce.boolean().optional(),
})

const numberFilterSchema = fieldBaseSchema.extend({
  type: z.literal("number"),
  min: z.coerce.number().optional(),
  max: z.coerce.number().optional(),
  defaultValue: z.coerce.number().optional(),
})

const filterConfigSchema = z.discriminatedUnion("type", [
  textFilterSchema,
  autocompleteFilterSchema,
  selectFilterSchema,
  dateRangeFilterSchema,
  timeRangeFilterSchema,
  booleanFilterSchema,
  numberFilterSchema,
])

const columnSchema = z.object({
  fieldPath: z.string().min(1, "Field path is required"),
  displayName: z.string().min(1, "Display name is required"),
  order: z.coerce.number().default(0),
  defaultVisible: z.coerce.boolean().default(false),
  visible: z.coerce.boolean().default(true),
  defaultSort: z.enum(["asc", "desc"]).optional(),
  sortable: z.coerce.boolean().default(false),
  searchable: z.coerce.boolean().default(false),
  filterable: z.coerce.boolean().default(false),
  isCollection: z.coerce.boolean().default(false),
  sortConfig: z
    .object({
      asc: z.string().optional(),
      desc: z.string().optional(),
    })
    .optional(),
  filterConfig: filterConfigSchema.optional(),
})

export type Column = z.infer<typeof columnSchema>
export type AllFilterFields = z.infer<typeof filterConfigSchema>
export type CaseSearchConfig = Column[]
type FilterType = AllFilterFields["type"] | ""

type ColumnAction =
  | { type: "ADD"; column: Column }
  | { type: "REMOVE"; index: number }
  | { type: "UPDATE"; index: number; column: Column }
  | { type: "SET"; columns: Column[] }
  | { type: "MOVE"; fromIndex: number; toIndex: number }

interface FilterConfigFieldsProps {
  filterType: FilterType
  defaultValues?: AllFilterFields
}

interface ColumnFormDialogProps {
  mode: "add" | "edit"
  column?: Column
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (column: Column) => void
}

interface ColumnCardProps {
  column: Column
  isFirst: boolean
  isLast: boolean
  onEdit: () => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
}

interface CaseSearchTabProps {
  data: EnhancedConfigData
  config: CaseSearchConfig
}

const FILTER_TYPES: Array<{ value: FilterType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "autocomplete", label: "Autocomplete" },
  { value: "select", label: "Select" },
  { value: "date-range", label: "Date Range" },
  { value: "time-range", label: "Time Range" },
  { value: "boolean", label: "Boolean" },
  { value: "number", label: "Number" },
]

const COLUMN_FLAGS = [
  { name: "defaultVisible", label: "Default Visible" },
  { name: "visible", label: "Visible" },
  { name: "sortable", label: "Sortable" },
  { name: "searchable", label: "Searchable" },
  { name: "filterable", label: "Filterable" },
  { name: "isCollection", label: "Is Collection" },
] as const

function parseOptionalString(value: FormDataEntryValue | null): string | undefined {
  if (value == null || typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function parseOptionalNumber(value: FormDataEntryValue | null): number | undefined {
  const str = parseOptionalString(value)
  if (str == null) return undefined
  const num = Number(str)
  return isNaN(num) ? undefined : num
}

function parseCheckbox(value: FormDataEntryValue | null): boolean {
  return value === "on"
}

function parseSelectOptions(raw: string | undefined): Array<{ value: string; label?: string }> {
  if (raw == null || raw.length === 0) return []

  return raw
    .split("\n")
    .filter(Boolean)
    .map((line) => {
      const [value, label] = line.split(":").map((s) => s.trim())
      return label != null && label.length > 0 ? { value, label } : { value }
    })
}

function parseDateRangeOptions(raw: string | undefined): Array<{ name: string; diff: number }> | undefined {
  if (raw == null || raw.length === 0) return undefined

  return raw
    .split("\n")
    .filter(Boolean)
    .map((line) => {
      const [name, diff] = line.split(":").map((s) => s.trim())
      return { name, diff: parseInt(diff, 10) }
    })
}

function buildFilterConfig(formData: FormData, filterType: FilterType): AllFilterFields | undefined {
  if (filterType === "") return undefined

  const description = parseOptionalString(formData.get("filterDescription")) ?? ""
  const order = parseOptionalNumber(formData.get("filterOrder"))
  const isGroupEnd = parseCheckbox(formData.get("filterIsGroupEnd")) || undefined
  const baseConfig = { description, order, isGroupEnd }

  switch (filterType) {
    case "text":
      return {
        ...baseConfig,
        type: "text",
        maxLength: parseOptionalNumber(formData.get("textMaxLength")),
        placeholder: parseOptionalString(formData.get("textPlaceholder")),
        defaultValue: parseOptionalString(formData.get("textDefaultValue")),
      }

    case "autocomplete":
      return {
        ...baseConfig,
        type: "autocomplete",
        multiple: parseCheckbox(formData.get("autocompleteMultiple")) || undefined,
      }

    case "select":
      return {
        ...baseConfig,
        type: "select",
        options: parseSelectOptions(parseOptionalString(formData.get("selectOptions"))),
        multiple: parseCheckbox(formData.get("selectMultiple")) || undefined,
        defaultValue: parseOptionalString(formData.get("selectDefaultValue")),
        parseAs: parseOptionalString(formData.get("selectParseAs")) as "literal" | "string" | undefined,
      }

    case "date-range":
      return {
        ...baseConfig,
        type: "date-range",
        options: parseDateRangeOptions(parseOptionalString(formData.get("dateRangeOptions"))),
      }

    case "time-range":
      return { ...baseConfig, type: "time-range" }

    case "boolean": {
      const defaultValueStr = parseOptionalString(formData.get("booleanDefaultValue"))
      return {
        ...baseConfig,
        type: "boolean",
        placeholder: parseOptionalString(formData.get("booleanPlaceholder")),
        options: [
          { value: true as const, label: parseOptionalString(formData.get("booleanTrueLabel")) ?? "Yes" },
          { value: false as const, label: parseOptionalString(formData.get("booleanFalseLabel")) ?? "No" },
        ],
        defaultValue: defaultValueStr != null ? defaultValueStr === "true" : undefined,
      }
    }

    case "number":
      return {
        ...baseConfig,
        type: "number",
        min: parseOptionalNumber(formData.get("numberMin")),
        max: parseOptionalNumber(formData.get("numberMax")),
        defaultValue: parseOptionalNumber(formData.get("numberDefaultValue")),
      }

    default:
      return undefined
  }
}

function parseColumnFromForm(formData: FormData, filterType: FilterType): Column {
  const sortConfigAsc = parseOptionalString(formData.get("sortConfigAsc"))
  const sortConfigDesc = parseOptionalString(formData.get("sortConfigDesc"))
  const hasSortConfig = sortConfigAsc != null || sortConfigDesc != null

  const rawColumn = {
    fieldPath: parseOptionalString(formData.get("fieldPath")) ?? "",
    displayName: parseOptionalString(formData.get("displayName")) ?? "",
    order: parseOptionalNumber(formData.get("order")) ?? 0,
    defaultVisible: parseCheckbox(formData.get("defaultVisible")),
    visible: parseCheckbox(formData.get("visible")),
    defaultSort: parseOptionalString(formData.get("defaultSort")) as "asc" | "desc" | undefined,
    sortable: parseCheckbox(formData.get("sortable")),
    searchable: parseCheckbox(formData.get("searchable")),
    filterable: parseCheckbox(formData.get("filterable")),
    isCollection: parseCheckbox(formData.get("isCollection")),
    sortConfig: hasSortConfig ? { asc: sortConfigAsc, desc: sortConfigDesc } : undefined,
    filterConfig: buildFilterConfig(formData, filterType),
  }

  const result = columnSchema.safeParse(rawColumn)

  if (!result.success) {
    console.error("Column validation failed:", result.error.flatten())
    throw new Error("Invalid column data")
  }

  return result.data
}

function columnReducer(state: Column[], action: ColumnAction): Column[] {
  switch (action.type) {
    case "ADD":
      return [...state, action.column]

    case "REMOVE":
      return state.filter((_, i) => i !== action.index)

    case "UPDATE":
      return state.map((column, i) => (i === action.index ? action.column : column))

    case "SET":
      return action.columns

    case "MOVE": {
      const newState = [...state]
      const [movedItem] = newState.splice(action.fromIndex, 1)
      newState.splice(action.toIndex, 0, movedItem)
      return newState.map((col, idx) => ({ ...col, order: idx }))
    }

    default:
      return state
  }
}

const FilterConfigFields = React.memo(function FilterConfigFields({
  filterType,
  defaultValues,
}: FilterConfigFieldsProps) {
  if (filterType === "") return null

  const isMatchingType = defaultValues?.type === filterType

  return (
    <div className="space-y-3 rounded-lg border bg-muted p-4">
      <h4 className="font-medium">Filter Configuration</h4>

      <div className="space-y-2">
        <label className="text-sm font-medium">Description *</label>
        <Input
          name="filterDescription"
          defaultValue={defaultValues?.description}
          placeholder="Filter description"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <label className="text-sm font-medium">Order</label>
          <Input name="filterOrder" type="number" defaultValue={defaultValues?.order} placeholder="Optional" />
        </div>
        <label className="flex items-center gap-2 pt-8">
          <Checkbox name="filterIsGroupEnd" defaultChecked={defaultValues?.isGroupEnd} />
          <span className="text-sm font-medium">Is Group End</span>
        </label>
      </div>

      {filterType === "text" && (
        <>
          <Input
            name="textMaxLength"
            type="number"
            defaultValue={isMatchingType && defaultValues.type === "text" ? defaultValues.maxLength : undefined}
            placeholder="Max Length (optional)"
          />
          <Input
            name="textPlaceholder"
            defaultValue={isMatchingType && defaultValues.type === "text" ? defaultValues.placeholder : undefined}
            placeholder="Placeholder (optional)"
          />
          <Input
            name="textDefaultValue"
            defaultValue={isMatchingType && defaultValues.type === "text" ? defaultValues.defaultValue : undefined}
            placeholder="Default Value (optional)"
          />
        </>
      )}

      {filterType === "autocomplete" && (
        <label className="flex items-center gap-2">
          <Checkbox
            name="autocompleteMultiple"
            defaultChecked={
              isMatchingType && defaultValues.type === "autocomplete" ? defaultValues.multiple : undefined
            }
          />
          <span className="text-sm font-medium">Allow Multiple Selection</span>
        </label>
      )}

      {filterType === "select" && (
        <>
          <div className="space-y-2">
            <label className="text-sm font-medium">Options (one per line, format: value:label) *</label>
            <textarea
              name="selectOptions"
              className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder={"option1:Label 1\noption2:Label 2"}
              defaultValue={
                isMatchingType && defaultValues.type === "select"
                  ? defaultValues.options
                      .map((opt) => `${opt.value}${opt.label != null ? `:${opt.label}` : ""}`)
                      .join("\n")
                  : undefined
              }
              required
            />
          </div>
          <label className="flex items-center gap-2">
            <Checkbox
              name="selectMultiple"
              defaultChecked={isMatchingType && defaultValues.type === "select" ? defaultValues.multiple : undefined}
            />
            <span className="text-sm font-medium">Allow Multiple Selection</span>
          </label>
          <Input
            name="selectDefaultValue"
            defaultValue={
              isMatchingType && defaultValues.type === "select"
                ? Array.isArray(defaultValues.defaultValue)
                  ? defaultValues.defaultValue.join(",")
                  : defaultValues.defaultValue
                : undefined
            }
            placeholder="Default Value (optional)"
          />
          <Select
            name="selectParseAs"
            defaultValue={isMatchingType && defaultValues.type === "select" ? defaultValues.parseAs : undefined}
          >
            <SelectTrigger>
              <SelectValue placeholder="Parse As (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="literal">Literal</SelectItem>
              <SelectItem value="string">String</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}

      {filterType === "date-range" && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Options (one per line, format: name:diff)</label>
          <textarea
            name="dateRangeOptions"
            className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder={"Last 7 days:-7\nLast 30 days:-30"}
            defaultValue={
              isMatchingType && defaultValues.type === "date-range"
                ? defaultValues.options?.map((opt) => `${opt.name}:${opt.diff}`).join("\n")
                : undefined
            }
          />
        </div>
      )}

      {filterType === "boolean" && (
        <>
          <Input
            name="booleanPlaceholder"
            defaultValue={isMatchingType && defaultValues.type === "boolean" ? defaultValues.placeholder : undefined}
            placeholder="Placeholder (optional)"
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              name="booleanTrueLabel"
              defaultValue={
                isMatchingType && defaultValues.type === "boolean" ? defaultValues.options[0]?.label : "Yes"
              }
              placeholder="True label"
            />
            <Input
              name="booleanFalseLabel"
              defaultValue={isMatchingType && defaultValues.type === "boolean" ? defaultValues.options[1]?.label : "No"}
              placeholder="False label"
            />
          </div>
          <Select
            name="booleanDefaultValue"
            defaultValue={
              isMatchingType && defaultValues.type === "boolean" && defaultValues.defaultValue !== undefined
                ? defaultValues.defaultValue.toString()
                : undefined
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Default Value (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}

      {filterType === "number" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Input
              name="numberMin"
              type="number"
              defaultValue={isMatchingType && defaultValues.type === "number" ? defaultValues.min : undefined}
              placeholder="Min (optional)"
            />
            <Input
              name="numberMax"
              type="number"
              defaultValue={isMatchingType && defaultValues.type === "number" ? defaultValues.max : undefined}
              placeholder="Max (optional)"
            />
          </div>
          <Input
            name="numberDefaultValue"
            type="number"
            defaultValue={isMatchingType && defaultValues.type === "number" ? defaultValues.defaultValue : undefined}
            placeholder="Default Value (optional)"
          />
        </>
      )}
    </div>
  )
})

const ColumnFormDialog = React.memo(function ColumnFormDialog({
  mode,
  column,
  open,
  onOpenChange,
  onSubmit,
}: ColumnFormDialogProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const [filterType, setFilterType] = useState<FilterType>(column?.filterConfig?.type ?? "")

  const isEditMode = mode === "edit"
  const title = isEditMode ? "Edit Column" : "Add New Column"
  const submitLabel = isEditMode ? "Save Changes" : "Add Column"

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (formRef.current == null) return

      try {
        const formData = new FormData(formRef.current)
        const parsedColumn = parseColumnFromForm(formData, filterType)

        onSubmit(parsedColumn)

        if (isEditMode) {
          onOpenChange(false)
        } else {
          formRef.current.reset()
          setFilterType("")
          onOpenChange(false)
        }
      } catch (error) {
        console.error("Form submission failed:", error)
      }
    },
    [filterType, isEditMode, onSubmit, onOpenChange],
  )

  const formContent = (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Field Path *</label>
          <Input name="fieldPath" defaultValue={column?.fieldPath} placeholder="e.g., caseId" required />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Display Name *</label>
          <Input name="displayName" defaultValue={column?.displayName} placeholder="e.g., Case ID" required />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Order</label>
          <Input name="order" type="number" defaultValue={column?.order ?? 0} />
        </div>
      </div>

      <div className="space-y-3">
        {COLUMN_FLAGS.map(({ name, label }) => (
          <label key={name} className="flex items-center gap-2">
            <Checkbox name={name} defaultChecked={column?.[name]} />
            <span className="text-sm font-medium">{label}</span>
          </label>
        ))}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Default Sort</label>
        <Select name="defaultSort" defaultValue={column?.defaultSort}>
          <SelectTrigger>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="asc">Ascending</SelectItem>
            <SelectItem value="desc">Descending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Sort Config</label>
        <div className="grid grid-cols-2 gap-2">
          <Input name="sortConfigAsc" defaultValue={column?.sortConfig?.asc} placeholder="Asc expression" />
          <Input name="sortConfigDesc" defaultValue={column?.sortConfig?.desc} placeholder="Desc expression" />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Filter Type</label>
        <Select value={filterType} onValueChange={(value) => setFilterType(value as FilterType)}>
          <SelectTrigger>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            {FILTER_TYPES.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <FilterConfigFields filterType={filterType} defaultValues={column?.filterConfig} />

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!isEditMode && (
        <DialogTrigger asChild>
          <Button type="button" variant="outline" className="gap-2">
            <PlusIcon className="h-4 w-4" />
            Add Column
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  )
})

const ColumnCard = React.memo(function ColumnCard({
  column,
  isFirst,
  isLast,
  onEdit,
  onRemove,
  onMoveUp,
  onMoveDown,
}: ColumnCardProps) {
  return (
    <div className="flex items-center gap-4 rounded-lg border bg-muted p-4">
      <div className="flex flex-col gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={onMoveUp} disabled={isFirst} className="h-6 w-6 p-0">
          ↑
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onMoveDown} disabled={isLast} className="h-6 w-6 p-0">
          ↓
        </Button>
      </div>

      <Badge variant="outline" className="font-mono text-xs">
        {column.order ?? "-"}
      </Badge>

      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-foreground">{column.displayName}</h4>
          <code className="rounded bg-muted px-2 py-0.5 text-xs">{column.fieldPath}</code>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {column.defaultVisible && (
            <Badge variant="outline" className="bg-info/10 text-info">
              Default Visible
            </Badge>
          )}
          {column.visible !== false && (
            <Badge variant="outline" className="bg-green-50 text-green-700">
              Visible
            </Badge>
          )}
          {column.sortable && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700">
              Sortable
            </Badge>
          )}
          {column.searchable && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700">
              Searchable
            </Badge>
          )}
          {column.filterable && (
            <Badge variant="outline" className="bg-pink-50 text-pink-700">
              Filterable
            </Badge>
          )}
          {column.isCollection && (
            <Badge variant="outline" className="bg-indigo-50 text-indigo-700">
              Collection
            </Badge>
          )}
          {column.defaultSort != null && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
              Sort: {column.defaultSort}
            </Badge>
          )}
          {column.sortConfig != null && (
            <Badge variant="outline" className="bg-cyan-50 text-cyan-700">
              Sort Config
            </Badge>
          )}
          {column.filterConfig != null && (
            <Badge variant="outline" className="bg-teal-50 text-teal-700">
              Filter: {column.filterConfig.type}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onEdit} className="text-info hover:bg-info/10">
          <PencilIcon className="h-4 w-4" />
        </Button>

        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:bg-red-50">
              <TrashIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="left" className="w-80">
            <p className="text-sm">Delete column &quot;{column.displayName}&quot;?</p>
            <div className="flex items-center justify-end gap-3 pt-4">
              <PopoverClose asChild>
                <Button type="button" variant="ghost" size="sm">
                  Cancel
                </Button>
              </PopoverClose>
              <PopoverClose asChild>
                <Button type="button" variant="destructive" size="sm" onClick={onRemove}>
                  Delete
                </Button>
              </PopoverClose>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
})

const EmptyColumnsState = React.memo(function EmptyColumnsState() {
  return (
    <div className="rounded-lg border-2 border-dashed border-border py-12 text-center text-muted-foreground">
      <p className="text-sm">No columns configured</p>
      <p className="mt-1 text-xs">Click &quot;Add Column&quot; to create your first column</p>
    </div>
  )
})

export function CaseSearchTab({ data, config }: CaseSearchTabProps) {
  const fetcher = useFetcher<FetcherData>()
  const pendingAction = (fetcher.json as { action?: string } | undefined)?.action ?? null

  const initialColumns = useMemo(() => (Array.isArray(config) ? config : []), [config])
  const [columns, dispatch] = useReducer(columnReducer, initialColumns)
  const [editingColumn, setEditingColumn] = useState<{ column: Column; index: number } | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  React.useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.success === true && fetcher.data.action === "REMOVE_SECTION") {
      dispatch({ type: "SET", columns: [] })
    }
  }, [fetcher.state, fetcher.data])

  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER
      return orderA - orderB
    })
  }, [columns])

  const getOriginalIndex = useCallback(
    (column: Column) => columns.findIndex((c) => c.fieldPath === column.fieldPath),
    [columns],
  )

  const handleAddColumn = useCallback((column: Column) => {
    dispatch({ type: "ADD", column })
  }, [])

  const handleUpdateColumn = useCallback(
    (updatedColumn: Column) => {
      if (editingColumn != null) {
        dispatch({ type: "UPDATE", index: editingColumn.index, column: updatedColumn })
        setEditingColumn(null)
      }
    },
    [editingColumn],
  )

  const handleRemoveColumn = useCallback((index: number) => {
    dispatch({ type: "REMOVE", index })
  }, [])

  const handleMoveColumn = useCallback(
    (index: number, direction: "up" | "down") => {
      const column = columns[index]
      const sortedIndex = sortedColumns.findIndex((col) => col.fieldPath === column.fieldPath)

      if (direction === "up" && sortedIndex > 0) {
        const targetCol = sortedColumns[sortedIndex - 1]
        const targetIndex = getOriginalIndex(targetCol)
        dispatch({ type: "MOVE", fromIndex: index, toIndex: targetIndex })
      } else if (direction === "down" && sortedIndex < sortedColumns.length - 1) {
        const targetCol = sortedColumns[sortedIndex + 1]
        const targetIndex = getOriginalIndex(targetCol)
        dispatch({ type: "MOVE", fromIndex: index, toIndex: targetIndex })
      }
    },
    [columns, sortedColumns, getOriginalIndex],
  )

  const handleSave = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      fetcher.submit(
        {
          action: "SAVE_SECTION",
          organisation: data.loadedOrganisation,
          project: data.loadedProject,
          jsonPath: "$.caseSearch",
          value: columns,
          concurrency: data.existingConfig?.concurrency ?? "",
        },
        { method: "post", encType: "application/json" },
      )
    },
    [fetcher, data, columns],
  )

  const handleRemoveSection = useCallback(() => {
    fetcher.submit(
      {
        action: "REMOVE_SECTION",
        organisation: data.loadedOrganisation,
        project: data.loadedProject,
        jsonPath: "$.caseSearch",
        concurrency: data.existingConfig?.concurrency ?? "",
      },
      { method: "post", encType: "application/json" },
    )
  }, [fetcher, data])

  const isSaving = fetcher.state !== "idle"

  return (
    <div className="flex h-full flex-col">
      <form onSubmit={handleSave} className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-6">
            <div className="rounded-lg border  p-4">
              <div className="mb-4 flex items-center justify-between">
                <ColumnFormDialog
                  mode="add"
                  open={addDialogOpen}
                  onOpenChange={setAddDialogOpen}
                  onSubmit={handleAddColumn}
                />
              </div>

              {columns.length === 0 ? (
                <EmptyColumnsState />
              ) : (
                <div className="space-y-3">
                  {sortedColumns.map((column, sortedIndex) => {
                    const originalIndex = getOriginalIndex(column)
                    return (
                      <ColumnCard
                        key={`${column.fieldPath}-${sortedIndex}`}
                        column={column}
                        isFirst={sortedIndex === 0}
                        isLast={sortedIndex === sortedColumns.length - 1}
                        onEdit={() => setEditingColumn({ column, index: originalIndex })}
                        onRemove={() => handleRemoveColumn(originalIndex)}
                        onMoveUp={() => handleMoveColumn(originalIndex, "up")}
                        onMoveDown={() => handleMoveColumn(originalIndex, "down")}
                      />
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-t  p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button type="submit" disabled={isSaving} className="flex-1 bg-info hover:bg-info/90">
              {pendingAction === "SAVE_SECTION" && isSaving && (
                <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              <SaveIcon className="h-4 w-4" />
              Save Case Search Configuration
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={isSaving}
                >
                  {pendingAction === "REMOVE_SECTION" && isSaving ? (
                    <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircleIcon className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Case Search Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all column configurations and reset this tab to its default state.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button type="button" variant="destructive" size="sm" onClick={handleRemoveSection}>
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </form>

      {editingColumn != null && (
        <ColumnFormDialog
          mode="edit"
          column={editingColumn.column}
          open={editingColumn != null}
          onOpenChange={(open) => !open && setEditingColumn(null)}
          onSubmit={handleUpdateColumn}
        />
      )}
    </div>
  )
}
